import React from 'react';
import './loan-step.css';
import {
  ScrollReveal,
  ScrollRevealStagger,
  ScrollRevealItem,
} from './ScrollReveal';

const stepsData = [
  {
    step: 'STEP 01',
    title: 'Check Eligibility',
    desc: 'Enter basic details to instantly check if you meet our loan criteria.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
    class: 'lifecycle-purple',
  },
  {
    step: 'STEP 02',
    title: 'Secure Verification',
    desc: 'Upload your KYC documents through our safe, encrypted digital process.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
    ),
    class: 'lifecycle-coral',
  },
  {
    step: 'STEP 03',
    title: 'Smart Matching',
    desc: 'We match you with the best loan offer based on your profile and needs.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06-.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </svg>
    ),
    class: 'lifecycle-amber',
  },
  {
    step: 'STEP 04',
    title: 'Digital Approval',
    desc: 'Get instant digital approval with clear terms — no hidden charges.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
    class: 'lifecycle-teal',
  },
  {
    step: 'STEP 05',
    title: 'Instant Disbursal',
    desc: 'Loan amount is transferred directly to your bank account — fast and secure.',
    icon: (
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19" />
        <polyline points="19 12 12 19 5 12" />
      </svg>
    ),
    class: 'lifecycle-purple-dark',
  },
];

const getPipePath = (index, total) => {
  if (index === 0) {
    return 'M 100,80 L 100,30 A 15,15 0 0,1 115,15 L 200,15';
  }
  if (index === total - 1) {
    return 'M 0,15 L 85,15 A 15,15 0 0,1 100,30 L 100,80';
  }
  return 'M 0,15 L 85,15 A 15,15 0 0,1 100,30 L 100,80 M 100,80 L 100,30 A 15,15 0 0,1 115,15 L 200,15';
};

const LoanStep = () => {
  return (
    <section className="exact-lifecycle-wrapper" id="loan-steps">
      <div className="lifecycle-bg-orb-one" aria-hidden="true" />
      <div className="lifecycle-bg-orb-two" aria-hidden="true" />
      <div className="lifecycle-dotted-grid" aria-hidden="true" />

      <div className="exact-lifecycle-inner">
        <ScrollReveal as="header" className="exact-lifecycle-title" y={32}>
          <h2>
            How It <span className="title-cyan-accent">Works</span>
          </h2>
        </ScrollReveal>

        <ScrollRevealStagger
          className="exact-lifecycle-container"
          stagger={0.12}
          delayChildren={0.05}
        >
          {stepsData.map((node, index) => (
            <ScrollRevealItem
              key={node.step}
              className={`exact-lifecycle-card ${node.class}`}
              y={36}
            >
              <div className="exact-pipe-svg-holder" aria-hidden="true">
                <svg
                  width="100%"
                  height="80"
                  viewBox="0 0 200 80"
                  preserveAspectRatio="none"
                  fill="none"
                >
                  <path
                    d={getPipePath(index, stepsData.length)}
                    className="pipeline-dashed-track"
                  />
                </svg>
              </div>

              <div className="exact-badge-circle">
                <div className="exact-badge-inner-icon">{node.icon}</div>
              </div>

              <div className="exact-lifecycle-text-holder">
                <h3 className="exact-lifecycle-header-title">{node.title}</h3>
                <p className="exact-lifecycle-paragraph-desc">{node.desc}</p>
              </div>

              <div className="exact-lifecycle-banner-base">
                <span>{node.step}</span>
                {index === stepsData.length - 1 && (
                  <div className="chevron-arrow-tip-mesh" aria-hidden="true" />
                )}
              </div>
            </ScrollRevealItem>
          ))}
        </ScrollRevealStagger>
      </div>
    </section>
  );
};

export default LoanStep;
